/*
app.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

// import './polyfills'
import './App.scss'
import { BrowserRouter, Route, Routes } from 'react-router'
import { useEffect, lazy, Suspense } from 'react'
import { Spinner } from './components/Spinner'
import { setSessionVar } from './modules/browserStorage'
import { sessionVar } from './constants/storage.const'
import { deviceUrl } from './modules/util'
import SelectProfile from './routes/RedactionType/selectProfile'
import ProfileItems from './routes/RedactionType/profileItems'
import SelectRedactionItems from './routes/RedactionType/selectRedactionItems'
import AddCustomWord from './routes/RedactionType/AddCustomWord'
import SavePreset from './routes/RedactionType/savePreset'
import SelectLocation from './routes/redactProfile/selectLocation'
import { useTranslation } from 'react-i18next'
const Scan = lazy(() => import('./routes/Scan/index'))
const Home = lazy(() => import('./routes/Home'))
const RedactionType = lazy(() => import('./routes/RedactionType/index'))
const Recipient = lazy(() => import('./routes/EmailRecipients/recipient'))
const InfoModal = lazy(() => import('./components/Modals/infoModal'))
const ToastContainer = lazy(() => import('./components/Toast/toastContainer'))
const EmailSubject = lazy(() => import('./routes/Scan/EmailSubject'))
const Preview = lazy(() => import('./routes/Preview'))
const Disclaimer = lazy(() => import('./routes/Disclaimer'))
const DisclaimerContent = lazy(
  () => import('./routes/Disclaimer/disclaimerContent')
)

export default function App() {
  useEffect(() => {
    setSessionVar(sessionVar.deviceUrl, deviceUrl)
    document.body.classList.add('using-mouse')
  }, [])
  const { t } = useTranslation()

  const routeFallback = (
    <div className='row mx-0 justify-content-center align-items-center h-100vh'>
      <Spinner size={170} text={t('LOADING')} />
    </div>
  )
  const withRouteSuspense = (element: JSX.Element) => (
    <Suspense fallback={routeFallback}>{element}</Suspense>
  )
  return (
    <BrowserRouter>
      <Routes>
        <Route path='/' element={withRouteSuspense(<Home />)} key='home' />
        <Route
          path='/disclaimer'
          element={withRouteSuspense(<Disclaimer />)}
          key='disclaimer'
        />
        <Route
          path='/disclaimer/:disclaimerType'
          element={withRouteSuspense(<DisclaimerContent />)}
          key='disclaimercontent'
        />
        <Route path='/scan' element={withRouteSuspense(<Scan />)} key='scan' />
        <Route
          path='/redactionType'
          element={withRouteSuspense(<RedactionType />)}
          key='redactiontype'
        />
        <Route
          path='/redactionType/preset'
          element={withRouteSuspense(<SelectProfile />)}
          key='presetredactiontype'
        />
        <Route
          path='/redactionItems'
          element={withRouteSuspense(<ProfileItems />)}
          key='profileItems'
        />
        <Route
          path='/redactionItems/edit'
          element={withRouteSuspense(<SelectRedactionItems />)}
          key='editProfileItems'
        />
        <Route
          path='/redactionItems/save'
          element={withRouteSuspense(<SavePreset />)}
          key='saveProfileItems'
        />
        <Route
          path='/redactionItems/custom/add'
          element={withRouteSuspense(<AddCustomWord />)}
          key='customProfileItems'
        />
        <Route
          path='/redactionItems/custom/edit/:index'
          element={withRouteSuspense(<AddCustomWord />)}
          key='customProfileItemsEdit'
        />
        <Route
          path='/redactProfile/selectLocation'
          element={withRouteSuspense(<SelectLocation />)}
          key='selectLocation'
        />
        <Route
          path='/preview'
          element={withRouteSuspense(<Preview />)}
          key='preview'
        />
        <Route
          path='/emailRecipient'
          element={withRouteSuspense(<Recipient />)}
          key='emailRecipient'
        />
        <Route
          path='/emailSubject'
          element={withRouteSuspense(<EmailSubject />)}
          key='emailsubject'
        />
      </Routes>
      <Suspense fallback={null}>
        <InfoModal id='info-modal' />
        <ToastContainer />
      </Suspense>
    </BrowserRouter>
  )
}
